import { useEffect, useState } from 'react';
import {
  LINK_TARGETS,
  releaseStage,
  type LinkChannel,
  type LinkRelease,
  type LinkTarget,
} from '../../../../scripts/lib/link-release.mjs';
import { resolveUpdateManifest } from '../../../../scripts/lib/link-updates.mjs';
import { detectLinkPlatform, type LinkPlatform } from '../lib/linkPlatform';
import './link-downloads.css';

function DownloadCard({
  download,
  beta,
}: {
  download: LinkRelease['downloads'][number];
  beta: boolean;
}) {
  return (
    <div className="link-downloads__platform">
      <a className="mh-button" href={download.url}>
        Download {LINK_TARGETS[download.target].label}
        {beta ? ' beta' : ''}
      </a>
      <p className="link-downloads__size">{(download.size / 1024 / 1024).toFixed(1)} MB</p>
      {download.target === 'win-x64' && (
        <p>
          Windows{beta ? ' beta' : ''}: available logger controls depend on this build and a local
          connection to the MyChron. Some test builds show shared history without enabling logger
          setup or recovery. Follow the setup options shown in Link.
        </p>
      )}
      {!download.signed && (
        <p className="link-downloads__notice">
          This Windows {beta ? 'beta' : 'build'} is not code-signed. Windows may show an
          unknown-publisher warning.
        </p>
      )}
      <details>
        <summary>Verify this download</summary>
        <p>SHA-256</p>
        <code>{download.sha256}</code>
      </details>
    </div>
  );
}

export function LinkDownloads() {
  const channel = window.__PADDIQ_CONFIG__?.linkReleaseChannel;
  const manifestUrl =
    window.__PADDIQ_CONFIG__?.linkDownloadManifestUrl?.trim() ||
    (import.meta.env.DEV && channel === 'dev'
      ? (import.meta.env.VITE_LINK_DOWNLOAD_MANIFEST_URL?.trim() ?? '')
      : '');
  const [release, setRelease] = useState<LinkRelease | null>(null);
  const [loading, setLoading] = useState(Boolean(manifestUrl));
  const [platform, setPlatform] = useState<LinkPlatform | null>(null);
  const [chosenTarget, setChosenTarget] = useState<LinkTarget | ''>();
  const environment =
    channel === 'dev' ? 'Development' : channel === 'beta' ? 'Production' : 'Unconfigured';

  useEffect(() => {
    let active = true;
    void detectLinkPlatform(navigator).then((detected) => {
      if (active) setPlatform(detected);
    });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 10000);
    setRelease(null);
    setLoading(Boolean(manifestUrl));
    if (!manifestUrl || !['dev', 'beta'].includes(channel ?? '')) {
      setLoading(false);
      return () => {
        window.clearTimeout(timeout);
        controller.abort();
      };
    }
    void (async () => {
      try {
        const url = new URL(manifestUrl);
        if (
          url.protocol !== 'https:' ||
          url.username ||
          url.password ||
          url.search ||
          url.hash ||
          !url.pathname.endsWith(`/paddiq-link/${channel}/current.json`)
        )
          throw new Error('Invalid release configuration');
        const response = await fetch(url, {
          cache: 'no-store',
          credentials: 'omit',
          redirect: 'error',
          signal: controller.signal,
        });
        if (!response.ok) throw new Error('Release unavailable');
        const value = resolveUpdateManifest(
          await response.json(),
          channel as LinkChannel,
          url.href,
        );
        if (!controller.signal.aborted) setRelease(value);
      } catch {
        setRelease(null);
      } finally {
        setLoading(false);
        window.clearTimeout(timeout);
      }
    })();
    return () => {
      window.clearTimeout(timeout);
      controller.abort();
    };
  }, [channel, manifestUrl]);

  const beta = !release || releaseStage(release.channel, release.version) !== 'stable';
  const reportSubject = `Paddiq Link ${environment}${beta ? ' beta' : ''}${release ? ` ${release.version}` : ''}`;
  const target = chosenTarget ?? platform?.target;
  const primary = release?.downloads.find((download) => download.target === target);
  const others = release?.downloads.filter((download) => download !== primary) ?? [];
  return (
    <section className="link-downloads" aria-labelledby="link-download-heading" id="download">
      <p className="mh-kicker">{beta ? 'Beta' : 'Paddiq Link'}</p>
      <h2 id="link-download-heading">Download Paddiq Link{beta ? ' Beta' : ''}</h2>
      {beta && (
        <p>
          Beta software is still being tested. Please report issues and keep your original session
          files.
        </p>
      )}
      <div aria-live="polite">
        {loading ? (
          <p>Checking for a beta release…</p>
        ) : !release ? (
          <p>The beta download is not available right now. Please check back or contact support.</p>
        ) : (
          <>
            <p>
              <strong>Version {release.version}</strong>
            </p>
            {!platform && <p>Checking your computer.</p>}
            {platform?.kind === 'mac' && (
              <div className="link-downloads__choice">
                <label htmlFor="link-mac-processor">Mac processor</label>
                <p id="link-mac-help">
                  {platform.target ? 'Check your processor' : 'Choose your processor'} in Apple menu
                  &gt; About This Mac.
                </p>
                <select
                  id="link-mac-processor"
                  aria-describedby="link-mac-help"
                  value={target ?? ''}
                  onChange={(event) =>
                    setChosenTarget(event.currentTarget.value as LinkTarget | '')
                  }
                >
                  <option value="">Choose Apple silicon or Intel</option>
                  <option value="mac-arm64">Apple silicon</option>
                  <option value="mac-x64">Intel</option>
                </select>
              </div>
            )}
            {platform?.kind === 'mobile' && (
              <p>
                Paddiq Link requires a Mac or Windows computer. Open this page on your computer, or
                open Other downloads to see the desktop installers.
              </p>
            )}
            {platform?.kind === 'unknown' && (
              <p>
                We couldn't identify your computer. Open Other downloads to choose an installer for
                macOS or Windows.
              </p>
            )}
            {(platform?.kind === 'unsupported' || (platform?.kind === 'windows' && !target)) && (
              <p>
                We couldn't match an installer to this computer. Other downloads lists the supported
                macOS and Windows x64 builds.
              </p>
            )}
            {primary ? (
              <DownloadCard download={primary} beta={beta} />
            ) : target ? (
              <p>
                The {LINK_TARGETS[target].label}
                {beta ? ' beta' : ''} download is not available for this release.
              </p>
            ) : null}
            {others.length > 0 && (
              <details className="link-downloads__others">
                <summary>Other downloads</summary>
                <div className="link-downloads__platforms">
                  {others.map((download) => (
                    <DownloadCard download={download} beta={beta} key={download.target} />
                  ))}
                </div>
              </details>
            )}
            <p>
              Downloads require a desktop computer. Install your first version from this page. Open
              Paddiq Link updates in the app for later versions. An update can download
              automatically; choose Restart and update when it is ready. Follow the{' '}
              <a href="/resources/paddiq-link-troubleshooting#updates">update guide</a> if an update
              cannot finish.
            </p>
          </>
        )}
      </div>
      <p>
        New to Link? Start with the <a href="/resources/paddiq-link">setup guide</a>, then{' '}
        <a href="/resources/paddiq-link-sessions">choose the sessions to upload</a>.
      </p>
      <p>
        <a href={`mailto:support@paddiq.app?subject=${encodeURIComponent(reportSubject)}`}>
          Report an issue
        </a>{' '}
        or <a href="/support">contact Paddiq support</a>. Include the version, computer type, what
        happened, and steps to reproduce. Do not include passwords or session tokens.
      </p>
    </section>
  );
}
